import { Bold, Code, Heading, Italic, Link, List, ListChecks, ListOrdered, Quote, SquareCode } from "lucide-preact";
import type { ComponentChildren } from "preact";
import {
  blockQuote,
  bold,
  bulletList,
  checkboxItem,
  fencedCodeBlock,
  heading,
  inlineCode,
  italic,
  link,
  numberedList,
  type Selection,
} from "./markdownCommands";
import type { MarkdownEditorTheme } from "./markdownEditorTheme";

export type ToolbarCommand = {
  icon: ComponentChildren;
  title: string;
  run: (value: string, selection: Selection) => { value: string; selection: Selection };
};

export type EditorMode = "write" | "preview";

// The framework Tabs component renders a full-width underlined bar, which is too heavy for a
// compact editor toolbar, so this uses a small local segmented toggle for Write/Preview instead.
export const ModeToggle = ({ mode, theme, onChange }: { mode: EditorMode; theme: MarkdownEditorTheme; onChange: (mode: EditorMode) => void }) => (
  <div class={`inline-flex text-xs overflow-hidden ${theme.modeGroup}`}>
    {(["write", "preview"] as const).map((option) => (
      <button
        key={option}
        type="button"
        onClick={() => onChange(option)}
        class={`px-2.5 py-1 capitalize transition-colors ${mode === option ? theme.modeActive : theme.modeInactive}`}
      >
        {option}
      </button>
    ))}
  </div>
);

type ToolbarProps = {
  commands: ToolbarCommand[];
  theme: MarkdownEditorTheme;
  disabled?: boolean;
  onRun: (command: ToolbarCommand) => void;
};

export const Toolbar = ({ commands, theme, disabled, onRun }: ToolbarProps) => (
  <div class={`flex flex-wrap items-center gap-0.5 border border-b-0 px-1.5 py-1 ${theme.toolbar}`}>
    {commands.map((command, index) => (
      <button
        key={index}
        type="button"
        title={command.title}
        disabled={disabled}
        onClick={() => onRun(command)}
        class={`p-1.5 disabled:opacity-50 disabled:cursor-not-allowed ${theme.toolbarButton}`}
      >
        {command.icon}
      </button>
    ))}
  </div>
);

export const buildToolbarCommands = (iconSize: number): ToolbarCommand[] => [
  { icon: <Bold size={iconSize} />, title: "Bold", run: bold },
  { icon: <Italic size={iconSize} />, title: "Italic", run: italic },
  { icon: <Heading size={iconSize} />, title: "Heading", run: heading },
  { icon: <List size={iconSize} />, title: "Bullet list", run: bulletList },
  { icon: <ListOrdered size={iconSize} />, title: "Numbered list", run: numberedList },
  { icon: <ListChecks size={iconSize} />, title: "Checklist", run: checkboxItem },
  { icon: <Link size={iconSize} />, title: "Link", run: link },
  { icon: <Quote size={iconSize} />, title: "Quote", run: blockQuote },
  { icon: <Code size={iconSize} />, title: "Inline code", run: inlineCode },
  { icon: <SquareCode size={iconSize} />, title: "Code block", run: fencedCodeBlock },
];

export const shortcutCommand = (event: KeyboardEvent): ToolbarCommand["run"] | undefined => {
  const meta = event.metaKey || event.ctrlKey;
  if (!meta) {
    return undefined;
  }
  if (event.key === "b") {
    return bold;
  }
  if (event.key === "i") {
    return italic;
  }
  if (event.key === "k") {
    return link;
  }
  return undefined;
};
